import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IGalleryModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/GalleryModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { SearchValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SearchValue';
import { SidebarGrid } from 'elements/minute/sidebar/sidebarComponents/complex/SidebarGrid/SidebarGrid';
import { useDragAndDropImage } from 'hooks/map/useDragAndDropImage';
import {
    EImageCategories,
    getGalleryImages,
    useGetImageCategories
} from 'libs/images/ImageGalleryData';
import { EFonts, EHovers } from 'libs/styles/styling';
import { uniqBy } from 'lodash';
import { useContext, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    dragAndDropEnabled?: boolean;
    selectedImageUrl?: string;
    additionalImages?: string[];
    initialRowsVisible?: number;

    extraRowsVisibleOnMore?: number;
    hideOpenModal?: boolean;
    columns?: number;
    imageSize?: number;
    maxHeight?: number;
    onSelected?: (imageUrl: string) => void;

    hideSearch?: boolean;
    hideCategoryFilter?: boolean;
    initialCategory?: EImageCategories;

    highlightedCategoryIds?: string[];
}

export const ImageGallery = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ctxEdit = useContext(CtxEdit);
    const { selectedImageUrl } = props;
    const dragAndDropImage = useDragAndDropImage('image');
    const [selectedCategory, setSelectedCategory] = useState<EImageCategories>(
        props.initialCategory ?? EImageCategories.ALL
    );
    const { categories } = useGetImageCategories();
    const [searchText, setSearchText] = useState('');

    function openModal() {
        ctxModal.setModal({
            type: EModal.GALLERY,
            data: {
                selectedImageUrl: selectedImageUrl,
                additionalImages: props.additionalImages,
                onSelected: props.onSelected
            } as IGalleryModal
        });
    }

    let galleryData: {
        imageUrl: string;
        keywords: string[];
        category: EImageCategories[];
        withDot?: boolean;
    }[] = uniqBy(
        [
            ...(props.additionalImages?.map((e) => ({
                imageUrl: e,
                keywords: [],
                category: [EImageCategories.YOURS]
            })) ?? []),
            ...ctxEdit.map?.worldMapSets
                ?.map((e) => ({
                    imageUrl: e.imageUrl,
                    keywords: e.title
                        ?.split(' ')
                        ?.map((e) => e.trim())
                        .filter(Boolean),
                    category: [EImageCategories.CATEGORIES],
                    withDot: props.highlightedCategoryIds?.includes(e.worldMapSetId)
                }))
                .filter((e) => e.imageUrl)
        ],
        'imageUrl'
    );

    if (selectedCategory !== EImageCategories.YOURS) {
        galleryData = [...galleryData, ...getGalleryImages()];
    }

    //filter based on the category
    const categoryFilter = selectedCategory !== EImageCategories.ALL;
    if (categoryFilter) {
        galleryData = galleryData.filter((image) => image.category.includes(selectedCategory));
    }

    const filteredImages =
        searchText.length >= 3
            ? galleryData.filter((image) =>
                  image.keywords?.some((keyword) => keyword.includes(searchText))
              )
            : galleryData;

    const selectedIndex = filteredImages.findIndex((e) => e.imageUrl === selectedImageUrl);

    return (
        <S_ImageGallery className={props.className}>
            {!props.hideOpenModal && (
                <S_OpenModal
                    onClick={() => {
                        openModal();
                    }}
                >
                    <S_Icon icon={['fal', 'arrow-up-right-and-arrow-down-left-from-center']} />
                    <S_Text>{Localization.OPEN_GALLERY}</S_Text>
                </S_OpenModal>
            )}
            {!props.hideCategoryFilter && (
                <S_Categories>
                    {categories.map((category) => {
                        return (
                            <S_Category
                                $selected={category.key === selectedCategory}
                                key={category.key}
                                onClick={() => {
                                    setSelectedCategory(category.key);
                                }}
                            >
                                {category.label}
                            </S_Category>
                        );
                    })}
                </S_Categories>
            )}
            {!props.hideSearch && (
                <S_SearchValue
                    onChange={(newText) => {
                        setSearchText(newText);
                    }}
                />
            )}

            {filteredImages?.length > 0 && (
                <SidebarGrid
                    scrollRef={dragAndDropImage.containerRef}
                    items={filteredImages.map((image) => (
                        <S_ImageContainer key={image.imageUrl}>
                            <S_Image
                                src={image.imageUrl}
                                draggable={props.dragAndDropEnabled ?? false}
                                onDragStart={(event) => {
                                    dragAndDropImage.onDragStart({
                                        event,
                                        imageUrl: image.imageUrl
                                    });
                                }}
                                onClick={() => {
                                    props.onSelected?.(image.imageUrl);
                                }}
                                onDragEnd={() => {
                                    dragAndDropImage.onDragEnd();
                                }}
                                $size={props.imageSize ?? 65}
                            />
                            {image.withDot && <S_HighlightIcon icon={['fas', 'star']} />}
                        </S_ImageContainer>
                    ))}
                    initialRowsVisible={props.initialRowsVisible}
                    extraRowsVisibleOnMore={props.extraRowsVisibleOnMore}
                    columns={props.columns ?? 4}
                    selectedIndex={selectedIndex}
                    maxHeight={props.maxHeight ?? 350}
                />
            )}

            {!filteredImages.length && <Message type="info">{Localization.NOTHING_FOUND}</Message>}
        </S_ImageGallery>
    );
};

const S_ImageGallery = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    width: 100%;
`;

const S_OpenModal = styled.div`
    width: 100%;
    margin: 0 0 15px 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    &:hover {
        cursor: pointer;
        text-decoration: underline;
    }
`;

const S_Icon = styled(FontAwesomeIcon)`
    font-size: 13px;
    margin: 0 10px 0 0;
`;

const S_Text = styled.span`
    font-size: 13px;
    font-family: ${EFonts.Regular};
`;

const S_SearchValue = styled(SearchValue)`
    margin-bottom: 15px;
`;

const S_ImageContainer = styled.div`
    position: relative;
`;

const S_Image = styled.img<{ $size: number }>`
    width: ${(props) => props.$size}px;
    height: ${(props) => props.$size}px;
    max-width: 100%;
    object-fit: contain;
    padding: 9px;
`;

const S_Categories = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 0 0 10px 0;
`;

const S_Category = styled.div<{ $selected: boolean }>`
    padding: 5px 8px;
    border-radius: 7px;
    margin: 0 5px 5px 0;
    font-size: 13px;
    font-family: ${EFonts.Regular};
    background: ${(props) => (props.$selected ? '#F4F4F4' : '#f5f5f5')};
    border: 1px solid ${(props) => (props.$selected ? '#f1c584' : '#dadada')};
    box-shadow: ${(props) => (props.$selected ? 'none' : '0px 0px 20px 0px #dedede inset')};
    cursor: ${(props) => (props.$selected ? 'default' : 'pointer')};
    ${(props) => {
        if (!props.$selected) {
            return css`
                &:hover {
                    box-shadow: ${EHovers.LIGHT4};
                }
            `;
        }
    }}
`;

const S_HighlightIcon = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
    color: #000;
    position: absolute;
    right: 2px;
    bottom: 2px;
    border-radius: 100%;
`;
